import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FaBoxOpen, FaCamera, FaCheck, FaComments, FaPlus, FaStar, FaTimes } from 'react-icons/fa';
import Icon from '../../components/ui/Icon';
import logo from '../../assets/images/logo.svg';
import {
  getRatingInfo,
  RatingError,
  RatingInfo,
  submitRating,
} from '../../api/rating';
import styles from './Rate.module.css';

const WORDS = ['', 'Very bad', 'Bad', 'OK', 'Good', 'Excellent'];
const REASONS = [
  { value: 'late', label: 'Late' },
  { value: 'damaged', label: 'Box damaged' },
  { value: 'no_call', label: 'Driver did not call' },
  { value: 'other', label: 'Other' },
];
const MAX_PHOTOS = 5;
const MAX_BYTES = 10 * 1024 * 1024;

const POINTS = [
  { icon: FaStar, title: 'Takes only a few seconds', text: 'Tap a star and you are done.' },
  { icon: FaCamera, title: 'Add photos if you like', text: 'Show us how your boxes arrived.' },
  { icon: FaComments, title: 'Our team reads every review', text: 'It helps us improve our service.' },
];

type Photo = { file: File; url: string };

const Boxes: React.FC<{ boxes: string[]; className: string }> = ({ boxes, className }) => (
  <div className={className}>
    <div className={styles.boxIcon}>
      <Icon icon={FaBoxOpen} />
    </div>
    <div>
      <div className={styles.boxTitle}>
        {boxes.length} box{boxes.length === 1 ? '' : 'es'} delivered
      </div>
      <div className={styles.boxNumbers}>{boxes.join('  ·  ')}</div>
    </div>
  </div>
);

/** The customer's rating page: opened from the WhatsApp link, no login, rates once. */
const Rate: React.FC = () => {
  const { token = '' } = useParams();
  const [info, setInfo] = useState<RatingInfo | null>(null);
  const [badLink, setBadLink] = useState<string | null>(null);
  const [rating, setRating] = useState(0);
  const [reasons, setReasons] = useState<string[]>([]);
  const [comment, setComment] = useState('');
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const shown = useRef<Photo[]>([]);
  shown.current = photos;

  useEffect(() => {
    document.title = 'Rate your delivery | Sahari Cargo';
    // A private link: never for search engines.
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex';
    document.head.appendChild(robots);
    return () => {
      robots.remove();
      shown.current.forEach((p) => URL.revokeObjectURL(p.url));
    };
  }, []);

  useEffect(() => {
    getRatingInfo(token)
      .then(setInfo)
      .catch((e) =>
        setBadLink(
          e instanceof RatingError && e.status === 404
            ? 'This link is not valid.'
            : e instanceof RatingError
              ? 'Something went wrong. Please try again.'
              : 'Could not open this page. Please check your connection and try again.',
        ),
      );
  }, [token]);

  const low = rating > 0 && rating <= 2;
  const name = info?.customerName || 'Customer';

  const toggleReason = (value: string) =>
    setReasons((all) =>
      all.includes(value) ? all.filter((r) => r !== value) : [...all, value],
    );

  const addPhotos = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = '';
    setError(null);
    const fits = picked.filter((f) => f.size <= MAX_BYTES);
    if (fits.length < picked.length) setError('A photo is too large (10 MB at most).');
    const room = MAX_PHOTOS - photos.length;
    if (fits.length > room) setError(`Up to ${MAX_PHOTOS} photos.`);
    setPhotos([
      ...photos,
      ...fits.slice(0, room).map((file) => ({ file, url: URL.createObjectURL(file) })),
    ]);
  };

  const removePhoto = (photo: Photo) => {
    URL.revokeObjectURL(photo.url);
    setPhotos(photos.filter((p) => p !== photo));
  };

  const submit = async () => {
    if (!rating || sending) return;
    setSending(true);
    setError(null);
    try {
      setInfo(
        await submitRating(token, {
          rating,
          comment: comment.trim() || undefined,
          reasons: low && reasons.length ? reasons : undefined,
          photos: photos.map((p) => p.file),
        }),
      );
    } catch (e) {
      if (e instanceof RatingError && e.status === 409) {
        setInfo(await getRatingInfo(token).catch(() => info));
      } else if (e instanceof RatingError && e.status === 400) {
        setError(e.message);
      } else {
        setError('Could not send your rating. Please try again.');
      }
    } finally {
      setSending(false);
    }
  };

  const stars = (value: number, onPick?: (n: number) => void) => (
    <div className={onPick ? styles.stars : styles.starsSmall}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={!onPick}
          aria-label={`${n} star${n === 1 ? '' : 's'}`}
          className={n <= value ? styles.starOn : styles.star}
          onClick={() => onPick?.(n)}
        >
          <Icon icon={FaStar} />
        </button>
      ))}
    </div>
  );

  const brand = (
    <div className={styles.logo}>
      <img src={logo} alt="Sahari Cargo" />
    </div>
  );

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        <div className={styles.hero}>
          <div className={styles.heroTop}>
            <div>
              <h1 className={styles.title}>
                {info?.rated ? 'Thank you!' : 'How was your delivery?'}
              </h1>
              <p className={styles.sub}>
                <span className={styles.deskOnly}>Your feedback helps us serve you better.</span>
                <span className={styles.smallOnly}>
                  {info && !info.rated
                    ? `Dear ${name}, your shipment has been delivered.`
                    : 'Your feedback helps us serve you better.'}
                </span>
              </p>
            </div>
            {brand}
          </div>
          <div className={styles.points}>
            {POINTS.map((p) => (
              <div key={p.title} className={styles.point}>
                <div className={styles.boxIcon}>
                  <Icon icon={p.icon} />
                </div>
                <div>
                  <div className={styles.boxTitle}>{p.title}</div>
                  <div className={styles.pointText}>{p.text}</div>
                </div>
              </div>
            ))}
          </div>
          {info && <Boxes boxes={info.boxes} className={styles.boxesOnRed} />}
        </div>

        <div className={styles.main}>
          {info && <Boxes boxes={info.boxes} className={styles.boxesOnWhite} />}
          {badLink && <p className={styles.center}>{badLink}</p>}
          {!badLink && !info && <p className={styles.center}>Loading…</p>}

          {info?.rated && (
            <div className={styles.done}>
              <div className={styles.ok}>
                <Icon icon={FaCheck} />
              </div>
              <p className={styles.hi}>Thank you!</p>
              <p className={styles.muted}>Your rating has been received.</p>
              {stars(info.rating ?? 0)}
              {(info.rating ?? 0) >= 4 && info.googleReviewUrl && (
                <>
                  <p className={styles.googleText}>Happy with us? Share it on Google.</p>
                  <a
                    className={styles.google}
                    href={info.googleReviewUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Review us on Google
                  </a>
                </>
              )}
            </div>
          )}

          {info && !info.rated && (
            <>
              <div className={styles.deskOnly}>
                <p className={styles.hi}>Dear {name},</p>
                <p className={styles.muted}>
                  Your shipment has been delivered. How was your experience?
                </p>
              </div>

              <div className={styles.columns}>
                <div className={styles.rateBox}>
                  <p className={styles.rateTitle}>Rate your experience</p>
                  {stars(rating, setRating)}
                  <p className={styles.word}>{WORDS[rating]}</p>
                </div>

                <div className={styles.fields}>
                  {low && (
                    <>
                      <p className={styles.label}>What went wrong?</p>
                      <div className={styles.pills}>
                        {REASONS.map((r) => (
                          <button
                            key={r.value}
                            type="button"
                            className={reasons.includes(r.value) ? styles.pillOn : styles.pill}
                            onClick={() => toggleReason(r.value)}
                          >
                            {r.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}

                  <label className={styles.label} htmlFor="rate-comment">
                    {low ? (
                      info.boxes.length > 1 ? 'Which box, and what happened?' : 'What happened?'
                    ) : (
                      <>
                        Write a review <span>(optional)</span>
                      </>
                    )}
                  </label>
                  <textarea
                    id="rate-comment"
                    className={styles.text}
                    value={comment}
                    maxLength={2000}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={low ? 'Tell us more…' : 'Tell us about your experience…'}
                  />

                  <p className={styles.label}>
                    Add photos <span>(optional)</span>
                  </p>
                  <div className={styles.photos}>
                    {photos.map((p) => (
                      <div key={p.url} className={styles.thumb}>
                        <img src={p.url} alt="Your upload" />
                        <button type="button" aria-label="Remove photo" onClick={() => removePhoto(p)}>
                          <Icon icon={FaTimes} />
                        </button>
                      </div>
                    ))}
                    {photos.length < MAX_PHOTOS && (
                      <button
                        type="button"
                        className={styles.add}
                        onClick={() => picker.current?.click()}
                      >
                        <Icon icon={FaPlus} />
                        Add
                      </button>
                    )}
                  </div>
                  <input
                    ref={picker}
                    type="file"
                    accept="image/jpeg,image/png"
                    multiple
                    hidden
                    onChange={addPhotos}
                  />
                  <p className={styles.hint}>Up to {MAX_PHOTOS} photos, JPG or PNG.</p>
                </div>
              </div>

              {error && <p className={styles.error}>{error}</p>}
              <div className={styles.footer}>
                <button
                  type="button"
                  className={styles.submit}
                  disabled={!rating || sending}
                  onClick={submit}
                >
                  {sending ? 'Sending…' : 'Submit'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Rate;
